<template>
  <div class="dialog">
    <div class="dialog-header">
      <div class="dialog-title">EDIT PLAYLIST</div>
    </div>

    <div class="dialog-content pb-0">
      <b-form
        class="playlist-form"
        @submit.prevent="editPlaylist">
        <b-form-group
          class="align-items-center"
          label="Name"
          label-for="name"
          label-cols="3">
          <b-form-input
            type="text"
            placeholder="Enter Playlist Name"
            autocomplete="off"
            size="sm"
            v-model="form.name"
            @input="$v.form.name.$touch()"
            :state="!$v.form.name.$error"
          ></b-form-input>
          <b-form-invalid-feedback>
            <span v-if="!$v.form.name.required">
              Name is required.
            </span>
            <span v-if="!$v.form.name.uniqueName && $v.form.name.required">
              Name is already used.
            </span>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          class="align-items-center"
          label="Premium"
          label-for="premium"
          label-cols="3">
          <toggle-button
            class="large-font mb-0"
            v-model="form.premium"
            :width="100"
            :height="35"
            :labels="{ checked : 'YES', unchecked : 'NO' }"
          />
        </b-form-group>

        <b-form-group
          class="align-items-center"
          label="Published"
          label-for="published"
          label-cols="3">
          <toggle-button
            class="large-font mb-0"
            v-model="form.published"
            :width="100"
            :height="35"
            :labels="{ checked : 'YES', unchecked : 'NO' }"
          />
        </b-form-group>

        <b-form-group
          class="align-items-center"
          label="Thumbnail"
          label-for="thumbnail"
          label-cols="3">
          <b-form-file
            accept="image/jpeg, image/png"
            v-model="form.thumbnail"
            @change="$v.form.thumbnail.$touch()"
            :state="!$v.form.thumbnail.$error"
          ></b-form-file>
          <b-form-invalid-feedback :state="!$v.form.thumbnail.$error">
            <span v-if="!$v.form.thumbnail.validThumbnail">
              Invalid file type.
            </span>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          class="align-items-center"
          label="Banner Display"
          label-for="bannerDisplay"
          label-cols="3">
          <toggle-button
            class="large-font mb-0"
            v-model="form.bannerDisplay"
            :width="100"
            :height="35"
            :labels="{ checked : 'YES', unchecked : 'NO' }"
          />
        </b-form-group>

        <b-form-group
          class="align-items-center"
          label="Banner"
          label-for="banner"
          label-cols="3"
          v-if="form.bannerDisplay">
          <b-form-file
            accept="image/jpeg, image/png"
            v-model="form.banner"
            @change="$v.form.banner.$touch()"
            :state="!$v.form.banner.$error"
          ></b-form-file>
          <b-form-invalid-feedback :state="!$v.form.banner.$error">
            <span v-if="!$v.form.banner.required">
              Banner is required.
            </span>
            <span v-if="!$v.form.banner.validBanner && $v.form.banner.required">
              Invalid file type.
            </span>
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button
            class="text-center"
            type="submit"
            variant="outline-success"
            :disabled="$v.form.$invalid || !isFormEdited">
            Submit
          </b-button>
          &nbsp;
          <b-button
            class="text-center ml-1"
            type="button"
            variant="outline-danger"
            @click="$emit('close')">
            Cancel
          </b-button>
        </div>
      </b-form>
    </div>

    <AppLoaderAdmin :isLoading="loading" />
  </div>
</template>

<script>
  import { required } from 'vuelidate/lib/validators';

  export default {
    props : {
      playlist : {
        type     : Object,
        required : true,
      },
    },
    data() {
      return {
        loading : false,
        form    : {
          name          : this.playlist.name,
          premium       : (this.playlist.premium == 1) ? true : false,
          published     : (this.playlist.published == 1) ? true : false,
          thumbnail     : null,
          banner        : null,
          bannerDisplay : Boolean(this.playlist.display_banner),
        },
        validBanner    : true,
        validThumbnail : true,
        isFormEdited   : false,
        uniqueName     : true,
        progress       : 0,
      }
    },
    components : {
      'AppLoaderAdmin' :
        () => import('../../../components/layout/AppLoaderAdmin'),
    },
    watch : {
      'form.name'(val) {
        this.uniqueName = true;
        if (val && this.playlist.name != val)
          this.checkPlaylistName();
      },
      'form.thumbnail'(val) {
        this.validThumbnail = true;
        if (!val.type.startsWith("image/")) {
          this.$nextTick(() => {
            this.validThumbnail = false;
          });
        }
      },
      'form.banner'(val) {
        this.validBanner = true;
        if (val && !val.type.startsWith("image/")) {
          this.$nextTick(() => {
            this.validBanner = false;
          });
        }
      },
    },
    methods : {

      /**
       * Edit Playlist
       */
      async editPlaylist() {
        this.loading = true;
        const formData = new FormData();
        const params = {};

        formData.append('plistId', this.$route.params.playlistId);

        if (this.form.name !== this.playlist.name)
          params.name = this.form.name;

        if (this.form.premium != Boolean(this.playlist.premium))
          params.premium = (this.form.premium) ? 1 : 0;

        if (this.form.published != Boolean(this.playlist.published))
          params.published = (this.form.published) ? 1 : 0;

        if (this.form.bannerDisplay != Boolean(this.playlist.display_banner))
          // eslint-disable-next-line camelcase
          params.display_banner = (this.form.bannerDisplay) ? 1 : 0;

        if (this.form.thumbnail)
          formData.append('thumbnail', this.form.thumbnail);

        if (this.form.banner && this.form.bannerDisplay)
          formData.append('banner', this.form.banner);
        
        formData.append('updateFields', JSON.stringify(params));

        try {
          const res = await this.$http.put('api/playlist', formData, {
            onUploadProgress : event =>
              this.progress = Math.round(event.loaded * 100 / event.total),
          });

          if (res) {
            this.$notify({
              group : 'notif',
              type  : 'success',
              title : 'Success',
              text  : 'Edited playlist successfully!',
            });
            this.$parent.$emit('update-playlist-info');
            this.$emit('close');
          }
        } catch(err) {
          this.loading = false;
          this.$notify({
            group : 'notif',
            type  : 'failed',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        }
      },

      /**
       * Check if Form is edited
       */
      formUpdated() {
        this.isFormEdited = false;
        if (this.form.name !== this.playlist.name ||
          this.form.published !== Boolean(this.playlist.published) ||
          this.form.premium !== Boolean(this.playlist.premium) ||
          (this.form.bannerDisplay !== Boolean(this.playlist.display_banner) ) ||
          this.form.thumbnail)
          this.isFormEdited = true;
          // console.log(this.isFormEdited, 'edited', (this.form.bannerDisplay !== Boolean(this.playlist.display_banner)));
      },

      /**
       * Check Playlist Name
       */
      checkPlaylistName : _.debounce(function() {
        this.$http.get('api/playlist/check', {
          params : {
            name : this.form.name,
          },
        }).then(response => {
          this.uniqueName = (response.data) ? false : true;
        }).catch(() => {});
      }, 500),
    },
    created() {
      this.$watch('form', this.formUpdated, {
        deep : true,
      });
    },
    validations() {
      return {
        form : {
          name : {
            required,
            uniqueName() {
              return this.uniqueName;
            },
          },
          premium   : { required },
          published : { required },
          thumbnail : {
            validThumbnail() {
              return this.validThumbnail;
            },
          },
          banner : (this.form.bannerDisplay === true &&
            this.playlist.display_banner === 0) ? {
            required,
            validBanner() {
              return this.validBanner;
            },
          } : {},
          bannerDisplay : { required },
        },
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../assets/scss/settings";

  .form-control {
    height: auto !important;
    font-size: auto !important;
  }

  .croppa-container {
    border: 1px dashed #068bcd;
  }
</style>